import { useState } from 'react'
import { AdminBlockEditor } from '@/components/block-editor/block-editor'
import type { BlockConfig, CreateThemePresetPayload, PlanSlug } from '@/types/admin'

const PLAN_TIERS: { value: PlanSlug | ''; label: string }[] = [
  { value: '',           label: 'Semua Plan' },
  { value: 'basic',     label: 'Basic+' },
  { value: 'pro',       label: 'Pro+' },
  { value: 'enterprise', label: 'Enterprise' },
]

const COLOR_FIELDS: { key: keyof CreateThemePresetPayload; label: string }[] = [
  { key: 'color_primary',      label: 'Warna Primer' },
  { key: 'color_secondary',    label: 'Warna Sekunder' },
  { key: 'color_accent',       label: 'Warna Aksen' },
  { key: 'color_background',   label: 'Latar Belakang' },
  { key: 'color_surface',      label: 'Surface' },
  { key: 'color_text_primary', label: 'Teks Utama' },
  { key: 'color_text_muted',   label: 'Teks Redup' },
  { key: 'color_border',       label: 'Border' },
]

const PAGE_TABS: { key: keyof CreateThemePresetPayload; label: string }[] = [
  { key: 'homepage_sections', label: 'Homepage' },
  { key: 'page_about',        label: 'About' },
  { key: 'page_events',       label: 'Events' },
  { key: 'page_members',      label: 'Members' },
  { key: 'page_music',        label: 'Music' },
  { key: 'page_shop',         label: 'Shop' },
]

export type ThemePresetFormValues = CreateThemePresetPayload

const DEFAULTS: ThemePresetFormValues = {
  name: '',
  description: null,
  thumbnail_url: null,
  is_active: true,
  sort_order: 0,
  palette_group: null,
  mode: 'any',
  min_plan_slug: null,
  color_primary:      '#7C3AED',
  color_secondary:    '#EC4899',
  color_accent:       '#F59E0B',
  color_background:   '#0F0F0F',
  color_surface:      '#1A1A2E',
  color_text_primary: '#FFFFFF',
  color_text_muted:   '#A1A1AA',
  color_border:       '#27272A',
  font_family_heading: 'Inter',
  font_family_body:    'Inter',
  border_radius_base:  '0.5rem',
  custom_css: null,
  homepage_sections: [],
  page_about:        [],
  page_events:       [],
  page_members:      [],
  page_music:        [],
  page_shop:         [],
}

interface ThemePresetFormProps {
  initialValues?: Partial<ThemePresetFormValues>
  onSubmit: (values: ThemePresetFormValues) => void
  isSaving: boolean
  saveError?: string | null
  submitLabel?: string
}

export function ThemePresetForm({
  initialValues,
  onSubmit,
  isSaving,
  saveError,
  submitLabel = 'Simpan',
}: ThemePresetFormProps) {
  const [form, setForm] = useState<ThemePresetFormValues>({ ...DEFAULTS, ...initialValues })
  const [activeTab, setActiveTab] = useState<'info' | 'layout'>('info')
  const [activePageTab, setActivePageTab] = useState<keyof CreateThemePresetPayload>('homepage_sections')

  function setField<K extends keyof ThemePresetFormValues>(key: K, value: ThemePresetFormValues[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) return
    onSubmit(form)
  }

  const totalLayoutBlocks = PAGE_TABS.reduce(
    (sum, { key }) => sum + (form[key] as BlockConfig[]).length,
    0
  )

  const tabClass = (tab: 'info' | 'layout') =>
    `px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
      activeTab === tab
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      {/* ── Top-level tab navigation ─────────────────────────────────────────── */}
      <div className="flex gap-0 border-b border-border mb-8">
        <button type="button" onClick={() => setActiveTab('info')} className={tabClass('info')}>
          Informasi &amp; Visual
        </button>
        <button type="button" onClick={() => setActiveTab('layout')} className={tabClass('layout')}>
          Layout Halaman
          {totalLayoutBlocks > 0 && (
            <span className="ml-1.5 rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-medium">
              {totalLayoutBlocks}
            </span>
          )}
        </button>
      </div>

      {/* ── Tab: Informasi & Visual ───────────────────────────────────────────── */}
      {activeTab === 'info' && (
        <div className="space-y-10">
          {/* Section 1: Info Dasar */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold border-b border-border pb-2">Informasi Dasar</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Nama Tema <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="cth. Dark Minimal"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Thumbnail URL</label>
                <input
                  type="url"
                  value={form.thumbnail_url ?? ''}
                  onChange={(e) => setField('thumbnail_url', e.target.value || null)}
                  placeholder="https://..."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-sm font-medium">Deskripsi</label>
                <textarea
                  value={form.description ?? ''}
                  onChange={(e) => setField('description', e.target.value || null)}
                  rows={2}
                  placeholder="Deskripsi singkat tema ini..."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Sort Order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setField('sort_order', Number(e.target.value))}
                  min={0}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Status</label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active ?? true}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <label htmlFor="is_active" className="text-sm text-muted-foreground">
                    Aktif (terlihat oleh tenant)
                  </label>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Grup Palet{' '}
                  <span className="text-muted-foreground font-normal">(opsional)</span>
                </label>
                <input
                  type="text"
                  value={form.palette_group ?? ''}
                  onChange={(e) => setField('palette_group', e.target.value || null)}
                  placeholder="cth. Fatiikan"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground">
                  Preset dengan grup yang sama dikelompokkan dalam satu folder.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Mode</label>
                <select
                  value={form.mode ?? 'any'}
                  onChange={(e) => setField('mode', e.target.value as 'dark' | 'light' | 'any')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="any">Any (tidak spesifik)</option>
                  <option value="dark">Dark Mode</option>
                  <option value="light">Light Mode</option>
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Digunakan untuk filter dark/light di dalam folder.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Minimum Plan</label>
                <select
                  value={form.min_plan_slug ?? ''}
                  onChange={(e) => setField('min_plan_slug', (e.target.value as PlanSlug) || null)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {PLAN_TIERS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Preset hanya tampil ke tenant dengan plan ini ke atas.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Visual */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold border-b border-border pb-2">Visual</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {COLOR_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <label className="text-sm font-medium">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={(form[key] as string) ?? '#000000'}
                      onChange={(e) => setField(key, e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background p-0.5"
                    />
                    <input
                      type="text"
                      value={(form[key] as string) ?? ''}
                      onChange={(e) => setField(key, e.target.value)}
                      placeholder="#000000"
                      maxLength={7}
                      className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              ))}

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Font Heading</label>
                <input
                  type="text"
                  value={form.font_family_heading}
                  onChange={(e) => setField('font_family_heading', e.target.value)}
                  placeholder="Inter"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Font Body</label>
                <input
                  type="text"
                  value={form.font_family_body}
                  onChange={(e) => setField('font_family_body', e.target.value)}
                  placeholder="Inter"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Border Radius</label>
                <input
                  type="text"
                  value={form.border_radius_base}
                  onChange={(e) => setField('border_radius_base', e.target.value)}
                  placeholder="0.5rem"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-sm font-medium">
                  Custom CSS <span className="text-muted-foreground font-normal">(opsional)</span>
                </label>
                <textarea
                  value={form.custom_css ?? ''}
                  onChange={(e) => setField('custom_css', e.target.value || null)}
                  rows={4}
                  placeholder=".hero { ... }"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                />
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ── Tab: Layout Halaman ───────────────────────────────────────────────── */}
      {activeTab === 'layout' && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Layout Halaman</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Atur block default untuk masing-masing halaman saat tema ini diterapkan.
            </p>
          </div>

          {/* Page sub-tabs */}
          <div className="flex flex-wrap gap-1 border-b border-border pb-0">
            {PAGE_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActivePageTab(key)}
                className={`rounded-t-md px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activePageTab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                {(form[key] as BlockConfig[]).length > 0 && (
                  <span className="ml-1.5 rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-medium">
                    {(form[key] as BlockConfig[]).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <AdminBlockEditor
            blocks={form[activePageTab] as BlockConfig[]}
            onChange={(blocks) => setField(activePageTab, blocks as ThemePresetFormValues[typeof activePageTab])}
          />
        </section>
      )}

      {/* ── Submit — selalu tampil di bawah ──────────────────────────────────── */}
      <div className="flex items-center gap-4 pt-6 mt-6 border-t border-border">
        <button
          type="submit"
          disabled={isSaving || !form.name.trim()}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSaving ? 'Menyimpan...' : submitLabel}
        </button>

        {saveError && (
          <p className="text-sm text-destructive">{saveError}</p>
        )}
      </div>
    </form>
  )
}
